import { BeneficiaryType, Currency } from '@seapoint/server/enums';
import { Avatar } from '../../../../design-system/avatar/avatar/avatar';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { cn } from '../../../../utils/cn';
import { getFormattedIban } from '../../../../utils/get-formatted-iban';
import { getFormattedSortCode } from '../../../../utils/get-formatted-sort-code';
import { getJointAvatarDescription } from '../invoice-data-section/utils/get-joint-avatar-description';

export type BeneficiaryCardVariant = 'SUGGESTED_BENEFICIARY' | 'NEW_BENEFICIARY';

type VariantConfig =
  | {
      variant: Extract<BeneficiaryCardVariant, 'SUGGESTED_BENEFICIARY'>;
      beneficiaryType: BeneficiaryType;
      currency: Currency;
      iban?: string;
      accountNumber?: string;
      sortCode?: string;
    }
  | {
      variant: Extract<BeneficiaryCardVariant, 'NEW_BENEFICIARY'>;
    };

type InvoiceDrawerBeneficiaryCardProps = {
  variantConfig: VariantConfig;
  name: string;
};

export const InvoiceDrawerBeneficiaryCard = ({ variantConfig, name }: InvoiceDrawerBeneficiaryCardProps) => {
  const { description } = getCardContent(variantConfig);

  return (
    <Inline space={3} className="w-full rounded-lg border-[0.5px] border-border bg-white p-4">
      <Avatar name={name} size="sm" />

      <Stack space={0} className="min-w-0 flex-1">
        <p className="text-sm leading-6 font-medium tracking-tight text-black">{name}</p>

        <p
          className={cn('text-xs leading-6 font-normal tracking-tight text-muted-foreground', {
            'text-chart-5': variantConfig.variant === 'NEW_BENEFICIARY',
          })}
        >
          {description}
        </p>
      </Stack>
    </Inline>
  );
};

const getCardContent = (variantConfig: VariantConfig) => {
  switch (variantConfig.variant) {
    case 'SUGGESTED_BENEFICIARY': {
      const description = getJointAvatarDescription({
        item1: variantConfig.currency,
        item2:
          variantConfig.beneficiaryType === BeneficiaryType.IBAN
            ? getFormattedIban(variantConfig.iban)
            : `${variantConfig.accountNumber} · ${getFormattedSortCode(variantConfig.sortCode)}`,
      });

      return {
        description,
      };
    }
    case 'NEW_BENEFICIARY':
      return {
        description: 'New Beneficiary',
      };
  }
};
